// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import { isActionOptionAServiceSecret } from 'flow/service/services';
import {
  actionHasRules,
  extractActionTypeFromId,
  generateSuppressionIntervalAsString,
  getDimensionNumber,
  getTagColorName,
  isActionAnImportedValue,
  isActionOfType,
  isDefaultPrecedenceIndexedAction,
} from 'flow/service/actions';
import { WEBHOOK_KEY } from 'flow/service/actionOptions';
import type { PredicatedProcessor } from '../../lib/predicatedProcessor';
import { createPredicatedProcessor } from '../../lib/predicatedProcessor';
import type {
  ServiceActionOptionChange,
  ThresholdAddedChange,
  Change,
  TagActionGlobalSettingsModifiedChange,
  TagActionLocalSettingsModifiedChange,
  ThresholdModifiedChange,
  ThresholdRemovedChange,
  ActionRuleGroupingModifiedChange,
  InputActionMetadataModifiedChange,
  OutputActionMetadataModifiedChange,
  OutputActionExpressionModifiedChange,
  OutcomeActionMetadataModifiedChange,
  SdkConfigurationAddedChange,
  SdkConfigurationRemovedChange,
  SdkConfigurationModifiedChange,
  PublishedAttributeAddedChange,
  ProcessingBuffer,
  ActionAddedChange,
  PublishedAttributeModifiedChange,
  ActionMLAAddedChange,
  ActionMLAModifiedChange,
  ActionMLARemovedChange,
  WorkflowRoutingActionMetadataModifiedChange,
  ChangelogInputBundle,
  ActionRemovedChange,
  MatrixModelActionMetadataModifiedChange,
  ServiceActionMetadataChange,
  FailureHandlingModeModifiedChange,
  OutputActionOptionsModifiedChange,
  MatrixModelActionOptionsModifiedChange,
} from '../../types';
import {
  Operation,
} from '../../types';

import type { BufferEntry } from '../types';
import {
  synthesizeSingleOperationForChanges,
  getRelevantVersionForOp, getRelevantWorkflowPropsForOp,
} from '../helpers';
import {
  buildEmptyLineEntry as _buildEmptyLineEntry,
  tryBuildEntries as _tryBuildEntries,
} from './helpers';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import type { Value } from './templates';
import type createTemplates from './templates';
import {
  FAKE_ACTION_ID_FOR_FAILURE_HANDLING_MODE,
  FAKE_ACTION_ID_FOR_SDK,
  OPTIONAL_ATTRIBUTE_FIELDS_MAP,
  SUPPORTED_ATTRIBUTE_FIELDS_MAP,
  SUPPORTED_PA_METADATA_FIELDS,
  VALUE_RANGE_OPTION_FIELD,
} from '../../constants';
import type {
  DataRoute, OutcomeObjectInTag, OutputValueRange, SuppressionInterval, Threshold,
} from '~/dashboard/views/workflows/store/engine/types';
import { getElementType } from '../../../general';

export function createChangeProcessors(args: {
  entriesBuffer: ProcessingBuffer;
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  templates: ReturnType<typeof createTemplates>;
}): Record<string, PredicatedProcessor<Change>> {
  const {
    entriesBuffer,
    A: {
      version: versionA,
      props: propsA,
    },
    B: {
      version: versionB,
      props: propsB,
    },
    templates,
  } = args;

  const versions = { versionA, versionB };
  const props = { propsA, propsB };

  // explicitly typed function for better generic type safety
  const createChangeProcessor = createPredicatedProcessor<Change>;
  // curried function to not have to pass `templates` down every time
  const buildEmptyLineEntry = (actionPublicId: string) => _buildEmptyLineEntry({
    actionPublicId,
    templates,
  });
  // curried function to not have to pass `templates` down every time
  const tryBuildEntries = (actionPublicId: string, fn: () => BufferEntry[]) => _tryBuildEntries({
    actionPublicId,
    templates,
    fn,
  });

  return {
    actionSectionTitleAndSubtitle: createChangeProcessor({
      predicates: [
        ({ actionPublicId }) => ![
          FAKE_ACTION_ID_FOR_FAILURE_HANDLING_MODE,
          FAKE_ACTION_ID_FOR_SDK,
        ].includes(actionPublicId),
      ],
      process: allChanges => {
        const changesByActionId = allChanges.reduce((changesRecord, change) => {
          const { actionPublicId } = change;
          if (!Object.hasOwn(changesRecord, actionPublicId)) {
            changesRecord[actionPublicId] = [];
          }
          changesRecord[actionPublicId].push(change);
          return changesRecord;
        }, {} as Record<string, Change[]>);


        const actionSectionTitleEntries: BufferEntry[] = Object.entries(changesByActionId)
          .flatMap(([actionPublicId, changesForAction]) => tryBuildEntries(actionPublicId, () => {
            const primaryOperationInChanges = synthesizeSingleOperationForChanges(changesForAction);
            const versionToReadFrom = getRelevantVersionForOp({
              operation: primaryOperationInChanges,
              versions,
            });

            const action = versionToReadFrom.actions[actionPublicId];
            const actionTitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.actionSectionTitle({
                action,
                operation: primaryOperationInChanges,
              }),
            };

            const emptyLineEntry = buildEmptyLineEntry(action.publicId);

            return isActionAnImportedValue(action)
              // there's never anything under an imported value title, so we add an empty line
              ? [actionTitleEntry, emptyLineEntry]
              : [actionTitleEntry];
          }));


        // builds the tagline under the action title in a separate loop for better readability
        const actionSubtitleEntries: BufferEntry[] = Object.entries(changesByActionId)
          .flatMap((
            [actionPublicId, changesForAction],
          ) => tryBuildEntries(actionPublicId, () => {
            const primaryOperationInChanges = synthesizeSingleOperationForChanges(changesForAction);
            const versionToReadFrom = getRelevantVersionForOp({
              operation: primaryOperationInChanges,
              versions,
            });

            const workflowPropsToReadFrom = getRelevantWorkflowPropsForOp({
              operation: primaryOperationInChanges,
              workflowProps: {
                propsA,
                propsB,
              },
            });

            const action = versionToReadFrom.actions[actionPublicId];
            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            if (isActionOfType(action, ActionType.SERVICE) && !actionHasRules(action)) {
              const serviceAlwaysRunsEntry: BufferEntry = {
                actionPublicId,
                line: templates.serviceAlwaysRunsText({ serviceAction: action }),
              };
              return [serviceAlwaysRunsEntry, emptyLineEntry];
            }

            if (isActionOfType(action, ActionType.OUTCOME)
              && isDefaultPrecedenceIndexedAction(action)) {
              const decisionToDefaultOutcomeEntry: BufferEntry = {
                actionPublicId,
                line: templates.outcomeDecisionToDefault({ outcomeAction: action }),
              };

              return [decisionToDefaultOutcomeEntry, emptyLineEntry];
            }

            if (isActionOfType(action, ActionType.WORKFLOW)
              && isDefaultPrecedenceIndexedAction(action)) {
              const runDefaultWorkflowEntry: BufferEntry = {
                actionPublicId,
                line: templates.workflowRoutingRunDefault({ workflowRoutingAction: action }),
              };

              return [runDefaultWorkflowEntry, emptyLineEntry];
            }

            if (
              !isActionOfType(action, ActionType.INPUT)
              && !isActionOfType(action, ActionType.OUTPUT)
              && !isActionOfType(action, ActionType.TAG)
            ) {
              const actionRuleEntry: BufferEntry = {
                actionPublicId,
                line: templates.actionRulesAsTextValue({
                  action,
                  workflowData: versionToReadFrom,
                  workflowProps: workflowPropsToReadFrom,
                }),
              };
              return [actionRuleEntry, emptyLineEntry];
            }

            return [];
          }));

        entriesBuffer.append(actionSectionTitleEntries);
        entriesBuffer.append(actionSubtitleEntries);
      },
    }),
    thresholdAddedOrRemoved: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ descriptor, op }) => descriptor === 'threshold' && op === Operation.Add,
        ({ descriptor, op }) => descriptor === 'threshold' && op === Operation.Remove,
      ],
      process: (
        addThresholdChanges: ThresholdAddedChange[],
        removeThresholdChanges: ThresholdRemovedChange[],
      ) => {
        const addedOrRemovedThresholdEntries: BufferEntry[] = [
          ...addThresholdChanges,
          ...removeThresholdChanges,
        ].flatMap(change => tryBuildEntries(change.actionPublicId, () => {
          const { actionPublicId, op, value } = change;

          const threshold = op === Operation.Add ? value.next : value.prev;
          const versionToReadFrom = getRelevantVersionForOp({ versions, operation: op });


          const thresholdOperationEntry: BufferEntry = {
            actionPublicId,
            line: templates.thresholdOperationSubtitle({ operation: op }),
          };

          const thresholdAsStringEntry: BufferEntry = {
            actionPublicId,
            line: templates.thresholdAsTextValue({
              threshold,
              version: versionToReadFrom,
              workflowProps: propsB,
            }),
          };

          const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

          return [thresholdOperationEntry, thresholdAsStringEntry, emptyLineEntry];
        }));


        entriesBuffer.append(addedOrRemovedThresholdEntries);
      },
    }),
    thresholdModified: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => op === Operation.Modify && descriptor === 'threshold',
      ],
      process: (modifyThresholdChanges: ThresholdModifiedChange[]) => {
        const modifiedThresholdEntries: BufferEntry[] = modifyThresholdChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const { actionPublicId, value } = change;

            const thresholdModifiedSubtitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.thresholdOperationSubtitle({ operation: Operation.Modify }),
            };

            const fromValueEntry: BufferEntry = {
              actionPublicId,
              line: templates.thresholdModifiedTextValue({
                threshold: value.prev,
                version: versionA,
                workflowProps: propsB,
                direction: 'from',
              }),
            };

            const toValueEntry: BufferEntry = {
              actionPublicId,
              line: templates.thresholdModifiedTextValue({
                threshold: value.next,
                version: versionB,
                workflowProps: propsB,
                direction: 'to',
              }),
            };

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            return [
              thresholdModifiedSubtitleEntry,
              fromValueEntry,
              emptyLineEntry,
              toValueEntry,
              emptyLineEntry,
            ];
          }));

        entriesBuffer.append(modifiedThresholdEntries);
      },
    }),
    serviceActionOptionsModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ descriptor }) => descriptor === 'action.service.credentials',
        ({ descriptor }) => descriptor === 'action.service.owner',
        ({ descriptor }) => descriptor === 'action.service.settings',
      ],
      process: (
        serviceActionCredentialChanges: ServiceActionOptionChange[],
        serviceActionOwnerChanges: ServiceActionOptionChange[],
        serviceActionSettingsChanges: ServiceActionOptionChange[],
      ) => {
        // peel out special render cases
        // TODO(BUILD-1899): add base url & modules specific handling
        const serviceSettingChanges: ServiceActionOptionChange[] = [];
        const serviceRecurringWorkflowChanges: ServiceActionOptionChange[] = [];

        for (const change of serviceActionSettingsChanges) {
          switch (change.optionName) {
            case WEBHOOK_KEY: {
              serviceRecurringWorkflowChanges.push(change);
              break;
            }
            default: {
              serviceSettingChanges.push(change);
            }
          }
        }
        // setup service action option changes to be built
        // render order can be changed by rearranging these
        type ServiceOptionChangeConfig = {
          changes: ServiceActionOptionChange[];
          subtitleTemplateBuilder: Function;
          entryContentTemplateBuilder: Function;
        };

        const serviceOptionChangeConfigs: ServiceOptionChangeConfig[] = [
          {
            changes: [
              ...(serviceActionCredentialChanges || []),
            ],
            subtitleTemplateBuilder: templates.serviceMetadataCredentialsSubtitle,
            entryContentTemplateBuilder: templates.serviceMetadataValueModified,
          },
          {
            changes: [
              ...(serviceActionOwnerChanges || []),
            ],
            subtitleTemplateBuilder: templates.serviceMetadataOwnersSubtitle,
            entryContentTemplateBuilder: templates.serviceMetadataValueModified,
          },
          {
            changes: [
              ...(serviceSettingChanges || []),
            ],
            subtitleTemplateBuilder: templates.serviceMetadataKeyValuesSubtitle,
            entryContentTemplateBuilder: templates.serviceMetadataValueModified,
          },
          {
            changes: [
              ...(serviceRecurringWorkflowChanges || []),
            ],
            subtitleTemplateBuilder: templates.serviceMetadataRecurringWorkflowSubtitle,
            entryContentTemplateBuilder: templates.serviceMetadataRecurringWorkflowContent,
          },
        ];
        // build service local settings changelog ui
        // first, iterate through every config
        const serviceOptionChanges = serviceOptionChangeConfigs.flatMap((
          serviceConfigArgs: ServiceOptionChangeConfig,
        ) => {
          const {
            changes, subtitleTemplateBuilder, entryContentTemplateBuilder,
          } = serviceConfigArgs;
          if (!changes) {
            return [];
          }
          // build the entries
          return changes.flatMap(
            change => tryBuildEntries(change.actionPublicId, () => {
              const {
                actionPublicId, op, optionName, value, isSecret, isOwner, enabledServiceConfig,
              } = change;
              const {
                prev: prevOptions,
                next: nextOptions,
              } = value;

              const getRenderedValue = (isPrev: boolean): String => {
                // recurring workflow case:
                // map the workflow id to the workflow's name
                if (optionName === WEBHOOK_KEY) {
                  const propsBasis = isPrev ? propsA : propsB;
                  const comparator = isPrev
                    ? Number(prevOptions.value)
                    : Number(nextOptions.value);
                  const recurringWorkflow = propsBasis.allWorkflows
                    .find(workflow => (
                      workflow.application_id === comparator
                    ));
                  return recurringWorkflow.name;
                }

                // all other cases
                return isPrev
                  ? String(prevOptions.value)
                  : String(nextOptions.value);
              };

              const contentEntry: BufferEntry = {
                actionPublicId,
                line: entryContentTemplateBuilder({
                  name: optionName,
                  op,
                  prev: prevOptions ? getRenderedValue(true) : null,
                  next: nextOptions ? getRenderedValue(false) : null,
                  isSecret,
                  isOwner,
                  enabledServiceConfig,
                }),
              };

              const subtitleEntry: BufferEntry = {
                actionPublicId,
                line: subtitleTemplateBuilder({ operation: change.op, enabledServiceConfig }),
              };

              return [
                subtitleEntry,
                contentEntry,
                buildEmptyLineEntry(actionPublicId),
              ];
            }));
        });
        entriesBuffer.append(serviceOptionChanges);
      },
    }),
    serviceCredentialOwnerModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ descriptor }) => descriptor === 'action.service.credentialOwner',
      ],
      process: (
        serviceActionMetadataChanges: ServiceActionMetadataChange[],
      ) => {
        type ServiceCredentialOwnerConfig = {
          changes: ServiceActionMetadataChange[];
          subtitleTemplateBuilder: Function;
          entryContentTemplateBuilder: Function;
        };

        const serviceOptionChangeConfigs: ServiceCredentialOwnerConfig[] = [
          {
            changes: [
              ...(serviceActionMetadataChanges || []),
            ],
            subtitleTemplateBuilder: templates.serviceMetadataCredentialOwnerSubtitle,
            entryContentTemplateBuilder: templates.serviceMetadataValueModified,
          },
        ];
        // build service action option changelog ui
        // first, iterate through every config
        const serviceOptionChanges = serviceOptionChangeConfigs.flatMap((
          serviceConfigArgs: ServiceCredentialOwnerConfig,
        ) => {
          const {
            changes, subtitleTemplateBuilder, entryContentTemplateBuilder,
          } = serviceConfigArgs;

          // build the entries
          return changes.flatMap(
            change => tryBuildEntries(change.actionPublicId, () => {
              const {
                actionPublicId, op, metadataName, value, enabledServiceConfig,
              } = change;
              const {
                prev,
                next,
              } = value;

              const action = versionB.actions[actionPublicId];
              const service = propsB.services[
                action.service.internalName
              ];

              const getRenderedValue = (isPrev: boolean): String => (isPrev
                ? String(prev)
                : String(next));

              const contentEntry: BufferEntry = {
                actionPublicId,
                line: entryContentTemplateBuilder({
                  name: metadataName,
                  op,
                  prev: prev ? getRenderedValue(true) : null,
                  next: next ? getRenderedValue(false) : null,
                  isSecret: isActionOptionAServiceSecret({
                    optionName: metadataName,
                    service,
                  }),
                  enabledServiceConfig,
                }),
              };

              const subtitleEntry: BufferEntry = {
                actionPublicId,
                line: subtitleTemplateBuilder({ operation: change.op }),
              };

              return [
                subtitleEntry,
                contentEntry,
                buildEmptyLineEntry(actionPublicId),
              ];
            }));
        });
        entriesBuffer.append(serviceOptionChanges);
      },
    }),
    tagActionLocalSettingsModified: createChangeProcessor({
      predicates: [
        ({ op, descriptor }) => op === Operation.Modify && descriptor === 'action.tag.localSettings',
      ],
      process: (tagLocalSettingsModifiedChanges: TagActionLocalSettingsModifiedChange[]) => {
        const entries: BufferEntry[] = tagLocalSettingsModifiedChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const { actionPublicId, value } = change;

            const {
              prev: prevLocalSettings,
              next: nextLocalSettings,
            } = value;

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            const modifiedLocalSettingsSubtitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.tagLocalSettingModifiedSubtitle(),
            };

            // service level failure default is the only local setting for now
            const modifiedServiceFailureDefaultValueEntryFrom: BufferEntry = {
              actionPublicId,
              line: templates.tagLocalSettingModifiedServiceFailureDefault({
                value: prevLocalSettings.serviceFailureDefault,
                direction: 'from',
              }),
            };

            const modifiedServiceFailureDefaultValueEntryTo: BufferEntry = {
              actionPublicId,
              line: templates.tagLocalSettingModifiedServiceFailureDefault({
                value: nextLocalSettings.serviceFailureDefault,
                direction: 'to',
              }),
            };

            return [
              modifiedLocalSettingsSubtitleEntry,
              modifiedServiceFailureDefaultValueEntryFrom,
              emptyLineEntry,
              modifiedServiceFailureDefaultValueEntryTo,
              emptyLineEntry,
            ];
          }));

        entriesBuffer.append(entries);
      },
    }),
    tagActionGlobalSettingsModified: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => op === Operation.Modify && descriptor === 'action.tag.globalSettings',
      ],
      process: (tagGlobalSettingsModifiedChanges: TagActionGlobalSettingsModifiedChange[]) => {
        const tagGlobalChangesEntries: BufferEntry[] = tagGlobalSettingsModifiedChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const {
              value: {
                prev: prevGlobalSettings,
                next: nextGlobalSettings,
              },
              actionPublicId,
            } = change;


            const globalSettingsForActionEntries: BufferEntry[] = Object
              .entries(nextGlobalSettings)
              .flatMap(([settingName, nextSettingValue]) => {
                const prevSettingValue = prevGlobalSettings[settingName];

                let prevSettingValueFormatted: Value;
                let nextSettingValueFormatted: Value;

                // some fields need special formatting. we do that here:
                switch (settingName) {
                  case 'color':
                    prevSettingValueFormatted = getTagColorName(prevSettingValue);
                    nextSettingValueFormatted = getTagColorName(nextSettingValue as string);
                    break;
                  case 'outcomes':
                    // outcomes names need to be grabbed from the outcomes array
                    prevSettingValueFormatted = (prevSettingValue as OutcomeObjectInTag[])
                      .map(o => o.outcome);

                    nextSettingValueFormatted = (nextSettingValue as OutcomeObjectInTag[])
                      .map(o => o.outcome);
                    break;
                  case 'suppressionInterval':
                    prevSettingValueFormatted = generateSuppressionIntervalAsString(
                      (prevSettingValue as SuppressionInterval),
                    );

                    nextSettingValueFormatted = generateSuppressionIntervalAsString(
                      (nextSettingValue as SuppressionInterval),
                    );
                    break;
                  default:
                    prevSettingValueFormatted = prevSettingValue;
                    nextSettingValueFormatted = nextSettingValue;
                    break;
                }


                return [{
                  actionPublicId,
                  line: templates.tagGlobalSettingsModifiedValue({
                    field: settingName,
                    next: nextSettingValueFormatted,
                    prev: prevSettingValueFormatted,
                  }),
                }];
              });


            const globalSettingSubtitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.tagGlobalSettingModifiedSubtitle(),
            };
            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            return [
              globalSettingSubtitleEntry,
              ...globalSettingsForActionEntries,
              emptyLineEntry,
            ];
          }));


        entriesBuffer.append(tagGlobalChangesEntries);
      },
    }),
    actionRuleGroupingModified: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => op === Operation.Modify && descriptor === 'action.rules.grouping',
      ],
      process: (ruleGroupingModifiedChanges: ActionRuleGroupingModifiedChange[]) => {
        const ruleGroupingModifiedEntries: BufferEntry[] = ruleGroupingModifiedChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const { actionPublicId } = change;

            const prevAction = versionA.actions[actionPublicId];
            const nextAction = versionB.actions[actionPublicId];

            const ruleGroupingChangedSubtitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.actionRulesGroupingModifiedSubtitle(),
            };

            const fromRuleAsTextEntry: BufferEntry = {
              actionPublicId,
              line: templates.actionRulesAsTextValue({
                action: prevAction,
                workflowData: versionA,
                workflowProps: propsA,
                direction: 'from',
              }),
            };

            const toRuleAsTextEntry: BufferEntry = {
              actionPublicId,
              line: templates.actionRulesAsTextValue({
                action: nextAction,
                workflowData: versionB,
                workflowProps: propsB,
                direction: 'to',
              }),
            };

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);
            return [
              ruleGroupingChangedSubtitleEntry,
              fromRuleAsTextEntry,
              emptyLineEntry,
              toRuleAsTextEntry,
              emptyLineEntry,
            ];
          }));

        entriesBuffer.append(ruleGroupingModifiedEntries);
      },
    }),
    actionMLAAddedOrRemoved: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ op, descriptor }) => op === Operation.Add && descriptor === 'action.rules.mla',
        ({ op, descriptor }) => op === Operation.Remove && descriptor === 'action.rules.mla',
      ],
      process: (
        actionMLAAddedChanges: ActionMLAAddedChange[],
        actionMLARemovedChanges: ActionMLARemovedChange[],
      ) => {
        const addedOrRemovedMLAEntries: BufferEntry[] = [
          ...actionMLAAddedChanges,
          ...actionMLARemovedChanges,
        ].flatMap(change => tryBuildEntries(change.actionPublicId, () => {
          const { actionPublicId, value: { next, prev }, op } = change;

          const mlaThresholdOrDataRoute = op === Operation.Add ? next : prev;
          const versionToReadFrom = getRelevantVersionForOp({
            operation: op,
            versions,
          });

          const workflowPropsToReadFrom = getRelevantWorkflowPropsForOp({
            operation: op,
            workflowProps: props,
          });

          const subtitleEntry: BufferEntry = {
            actionPublicId,
            line: templates.mlaOperationSubtitle({
              operation: op,
            }),
          };

          // prev or next can be used interchangeably
          const elementType = getElementType(mlaThresholdOrDataRoute);
          if (elementType !== 'dataroute' && elementType !== 'threshold') {
            throw new Error('MLA Source is not a valid element type');
          }

          const mlaDataBundle: Parameters<typeof templates.mlaAsTextValue>[0]['mlaDataBundle'] = (
            elementType === 'threshold'
              ? {
                source: 'threshold',
                threshold: mlaThresholdOrDataRoute as Threshold,
                thresholds: versionToReadFrom.thresholds,
              }
              : {
                source: 'dataroute',
                dataRoute: mlaThresholdOrDataRoute as DataRoute,
                dataRoutes: versionToReadFrom.dataRoutes,
              }
          );

          const mlaValueEntry: BufferEntry = {
            actionPublicId,
            line: templates.mlaAsTextValue({
              actions: versionToReadFrom.actions,
              services: workflowPropsToReadFrom.services,
              sourceAction: versionToReadFrom.actions[actionPublicId],
              mlaDataBundle,
              customLists: workflowPropsToReadFrom.customLists,
            }),
          };

          const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

          return [
            subtitleEntry,
            mlaValueEntry,
            emptyLineEntry,
          ];
        }));

        entriesBuffer.append(addedOrRemovedMLAEntries);
      },
    }),
    actionMLAModified: createChangeProcessor({
      predicates: [
        ({ op, descriptor }) => op === Operation.Modify && descriptor === 'action.rules.mla',
      ],
      process: (
        actionMLAModifiedChanges: ActionMLAModifiedChange[],
      ) => {
        const modifiedMLAEntries: BufferEntry[] = actionMLAModifiedChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const {
              actionPublicId,
              value: {
                prev: prevMLAThresholdOrDataRoute,
                next: nextMLAThresholdOrDataRoute,
              },
            } = change;

            const subtitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.mlaOperationSubtitle({
                operation: Operation.Modify,
              }),
            };

            // prev or next can be used interchangeably
            const elementType = getElementType(nextMLAThresholdOrDataRoute);
            if (elementType !== 'dataroute' && elementType !== 'threshold') {
              throw new Error('MLA Source is not a valid element type');
            }

            const prevMlaDataBundle: Parameters<typeof templates.mlaModifiedValue>[0]['mlaDataBundle'] = (
              elementType === 'threshold'
                ? {
                  source: 'threshold',
                  threshold: prevMLAThresholdOrDataRoute as Threshold,
                  thresholds: versionA.thresholds,
                }
                : {
                  source: 'dataroute',
                  dataRoute: prevMLAThresholdOrDataRoute as DataRoute,
                  dataRoutes: versionA.dataRoutes,
                }
            );


            const fromMlaValueEntry: BufferEntry = {
              actionPublicId,
              line: templates.mlaModifiedValue({
                mlaDataBundle: prevMlaDataBundle,
                direction: 'from',
                actions: versionA.actions,
                services: propsA.services,
                sourceAction: versionA.actions[actionPublicId],
                customLists: propsA.customLists,
              }),
            };

            const nextMlaDataBundle: Parameters<typeof templates.mlaModifiedValue>[0]['mlaDataBundle'] = (
              elementType === 'threshold'
                ? {
                  source: 'threshold',
                  threshold: prevMLAThresholdOrDataRoute as Threshold,
                  thresholds: versionB.thresholds,
                }
                : {
                  source: 'dataroute',
                  dataRoute: prevMLAThresholdOrDataRoute as DataRoute,
                  dataRoutes: versionB.dataRoutes,
                }
            );

            const toMlaValueEntry: BufferEntry = {
              actionPublicId,
              line: templates.mlaModifiedValue({
                mlaDataBundle: nextMlaDataBundle,
                direction: 'to',
                actions: versionB.actions,
                services: propsB.services,
                sourceAction: versionB.actions[actionPublicId],
                customLists: propsB.customLists,
              }),
            };

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            return [
              subtitleEntry,
              fromMlaValueEntry,
              emptyLineEntry,
              toMlaValueEntry,
              emptyLineEntry,
            ];
          }));

        entriesBuffer.append(modifiedMLAEntries);
      },
    }),
    inputOutputActionAdded: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ descriptor, op, value }) => op === Operation.Add && descriptor === 'action'
          && isActionOfType(value.next, ActionType.INPUT),
        ({ descriptor, op, value }) => op === Operation.Add && descriptor === 'action'
          && isActionOfType(value.next, ActionType.OUTPUT),
      ],
      process: (
        addedInputActionChanges: ActionAddedChange[],
        addedOutputActionChanges: ActionAddedChange[],
      ) => {
        const attributeMetadataEntries: BufferEntry[] = [
          ...addedInputActionChanges,
          ...addedOutputActionChanges,
        ].flatMap(change => tryBuildEntries(change.actionPublicId, () => {
          const {
            actionPublicId,
            value: {
              next: action,
            },
          } = change;

          const attributeType = extractActionTypeFromId(actionPublicId) === ActionType.INPUT
            ? ActionType.INPUT
            : ActionType.OUTPUT;

          const attributeMetadata = attributeType === ActionType.INPUT
            ? action.input
            : action.output;

          const subtitleTemplate = attributeType === ActionType.INPUT
            ? templates.inputActionMetadataValueNameSubtitle
            : templates.outputActionMetadataValueSubtitle;

          const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

          const supportedAttributeMetadataFields = SUPPORTED_ATTRIBUTE_FIELDS_MAP[attributeType];
          const optionalAttributeMetadataFields = OPTIONAL_ATTRIBUTE_FIELDS_MAP[attributeType];

          const metadataEntriesForAction = supportedAttributeMetadataFields.flatMap(field => {
            const isFieldOptional = optionalAttributeMetadataFields.includes(field);
            const fieldValue = attributeMetadata[field];

            // only create an entry for optional fields if they have a value
            const shouldCreateEntryForField = isFieldOptional
              ? Boolean(fieldValue)
              : true;

            if (!shouldCreateEntryForField) {
              return [];
            }

            const subtitleEntry: BufferEntry = {
              actionPublicId,
              line: subtitleTemplate({ field, op: Operation.Add }),
            };

            const valueEntry: BufferEntry = {
              actionPublicId,
              line: templates.attributeActionMetadataValue({
                value: fieldValue,
              }),
            };


            return [subtitleEntry, valueEntry, emptyLineEntry];
          });
          return metadataEntriesForAction;
        }));


        entriesBuffer.append(attributeMetadataEntries);
      },
    }),
    inputOrOutputActionMetadataModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ descriptor, op }) => op === Operation.Modify && descriptor === 'action.input.metadata',
        ({ descriptor, op }) => op === Operation.Modify && descriptor === 'action.output.metadata',
      ],
      process: (
        modifiedInputActionChanges: InputActionMetadataModifiedChange[],
        modifiedOutputActionChanges: OutputActionMetadataModifiedChange[],
      ) => {
        const attributeMetadataEntries: BufferEntry[] = [
          ...modifiedInputActionChanges,
          ...modifiedOutputActionChanges,
        ].flatMap(change => tryBuildEntries(change.actionPublicId, () => {
          const {
            actionPublicId, value: {
              prev: prevMetadata,
              next: nextMetadataByOp,
            },
          } = change;

          const attributeType = extractActionTypeFromId(actionPublicId) === ActionType.INPUT
            ? ActionType.INPUT
            : ActionType.OUTPUT;

          const subtitleTemplate = attributeType === ActionType.INPUT
            ? templates.inputActionMetadataValueNameSubtitle
            : templates.outputActionMetadataValueSubtitle;

          const emptyLineEntry = buildEmptyLineEntry(actionPublicId);
          // iterate through every operation:fields object entry
          return Object.entries(nextMetadataByOp)
            .flatMap(([operation, fieldsForOp]) => {
              // iterate through every fieldName:fieldValue object entries for the operation
              const entriesForAction = Object
                .entries(fieldsForOp).flatMap(([field, nextValue]) => {
                  const subtitleEntry: BufferEntry = {
                    actionPublicId,
                    // the cast is needed because .entries turns keys into type `string`
                    line: subtitleTemplate({ field, op: operation as Operation }),
                  };

                  let valueEntry: BufferEntry;
                  if (operation === Operation.Modify) {
                    valueEntry = {
                      actionPublicId,
                      line: templates.attributeMetadataModifiedValue({
                        prev: prevMetadata[field],
                        next: nextValue,
                      }),
                    };
                  } else {
                    valueEntry = {
                      actionPublicId,
                      line: templates.attributeActionMetadataValue({
                        value: nextValue,
                      }),
                    };
                  }

                  return [subtitleEntry, valueEntry, emptyLineEntry];
                });
              return entriesForAction;
            });
        }));


        entriesBuffer.append(attributeMetadataEntries);
      },
    }),
    outputActionOptionModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ descriptor, op }) => op === Operation.Modify && descriptor === 'action.output.options',
      ],
      process: (
        modifiedOutputActionOptionChanges: OutputActionOptionsModifiedChange[],
      ) => {
        const attributeMetadataEntries: BufferEntry[] = [
          ...modifiedOutputActionOptionChanges,
        ].flatMap(change => tryBuildEntries(change.actionPublicId, () => {
          const {
            actionPublicId, value: {
              prev: prevOptions,
              next: nextOptionsByOp,
            },
          } = change;

          const subtitleTemplate = templates.outputActionOptionsValueSubtitle;

          const emptyLineEntry = buildEmptyLineEntry(actionPublicId);
          // iterate through every operation:fields object entry
          return Object.entries(nextOptionsByOp)
            .flatMap(([operation, fieldsForOp]) => {
              // iterate through every fieldName:fieldValue object entries for the operation
              const entriesForAction = Object
                .entries(fieldsForOp).flatMap(([field, nextValue]) => {
                  if (field === VALUE_RANGE_OPTION_FIELD) {
                    const outputValueRangeEntries: BufferEntry[] = [];
                    const prevOutputValueRanges = prevOptions[field] ?? [];
                    const nextOutputValueRange = (nextValue ?? []) as OutputValueRange[];

                    const existingRangeRows = nextOutputValueRange
                      .slice(0, prevOutputValueRanges.length);
                    const newRangeRows = nextOutputValueRange
                      .slice(prevOutputValueRanges.length);
                    const deletedRangeRows = prevOutputValueRanges
                      .slice(nextOutputValueRange.length);

                    existingRangeRows.forEach((valueRangeEntry, index) => {
                      const prev = prevOutputValueRanges[index];
                      const didRangeRowChange = !isEqual(prev, valueRangeEntry);
                      if (prev && didRangeRowChange) {
                        outputValueRangeEntries.push({
                          actionPublicId,
                          line: templates.outputValueRangeRowSubtitle({
                            row: index + 1,
                            op: Operation.Modify,
                          }),
                        });
                        outputValueRangeEntries.push({
                          actionPublicId,
                          line: templates.outputValueRangeValueModified({
                            value: prev,
                            comparisonValue: valueRangeEntry,
                            comparisonSide: 'From',
                          }),
                        });
                        outputValueRangeEntries.push({
                          actionPublicId,
                          line: templates.outputValueRangeValueModified({
                            value: valueRangeEntry,
                            comparisonValue: prev,
                            comparisonSide: 'To',
                          }),
                        });
                      }
                    });

                    if (newRangeRows.length > 0) {
                      outputValueRangeEntries.push({
                        actionPublicId,
                        line: templates.outputValueRangeRowSubtitle({
                          op: Operation.Add,
                        }),
                      });
                      newRangeRows.forEach((valueRangeEntry, index) => {
                        outputValueRangeEntries.push({
                          actionPublicId,
                          line: templates.outputValueRangeValueAdded({
                            row: existingRangeRows.length + index + 1,
                            value: valueRangeEntry,
                          }),
                        });
                      });
                    }

                    if (nextOutputValueRange.length === 0) {
                      outputValueRangeEntries.push({
                        actionPublicId,
                        line: templates.outputValueRangeRemoved(),
                      });
                    } else if (deletedRangeRows.length > 0) {
                      outputValueRangeEntries.push({
                        actionPublicId,
                        line: templates.outputValueRangeRowSubtitle({
                          op: Operation.Remove,
                        }),
                      });
                      const removedRows = deletedRangeRows.map(
                        (_, index) => existingRangeRows.length + index + 1,
                      );
                      outputValueRangeEntries.push({
                        actionPublicId,
                        line: templates.outputValueRangeValueRemoved({
                          rows: removedRows,
                        }),
                      });
                    }

                    return [...outputValueRangeEntries, emptyLineEntry];
                  }

                  const subtitleEntry: BufferEntry = {
                    actionPublicId,
                    // the cast is needed because .entries turns keys into type `string`
                    line: subtitleTemplate({ field, op: operation as Operation }),
                  };

                  let valueEntry: BufferEntry;
                  if (operation === Operation.Modify) {
                    valueEntry = {
                      actionPublicId,
                      line: templates.attributeMetadataModifiedValue({
                        prev: prevOptions[field],
                        next: nextValue,
                      }),
                    };
                  } else {
                    valueEntry = {
                      actionPublicId,
                      line: templates.attributeActionMetadataValue({
                        value: nextValue as string,
                      }),
                    };
                  }

                  return [subtitleEntry, valueEntry, emptyLineEntry];
                });
              return entriesForAction;
            });
        }));


        entriesBuffer.append(attributeMetadataEntries);
      },
    }),
    outputActionExpressionModified: createChangeProcessor({
      predicates: [
        ({ op, descriptor }) => op === Operation.Modify && descriptor === 'action.output.expression',
      ],
      process: (
        outputExpressionChanges: OutputActionExpressionModifiedChange[],
      ) => {
        const outputExpressionEntries: BufferEntry[] = outputExpressionChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const {
              actionPublicId,
              value: {
                prev: prevExpression,
                next: nextExpression,
              },
            } = change;

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            const subtitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.outputExpressionModifiedSubtitle(),
            };

            const fromExpressionValueEntry: BufferEntry = {
              actionPublicId,
              line: templates.outputExpressionModifiedValue({
                actions: versionA.actions,
                dataRoutes: versionA.dataRoutes,
                expression: prevExpression,
                direction: 'from',
              }),
            };

            const toExpressionValueEntry: BufferEntry = {
              actionPublicId,
              line: templates.outputExpressionModifiedValue({
                actions: versionB.actions,
                dataRoutes: versionB.dataRoutes,
                expression: nextExpression,
                direction: 'to',
              }),
            };

            return [
              subtitleEntry,
              fromExpressionValueEntry,
              emptyLineEntry,
              toExpressionValueEntry,
              emptyLineEntry,
            ];
          }));

        entriesBuffer.append(outputExpressionEntries);
      },
    }),
    outcomeActionMetadataModified: createChangeProcessor({
      predicates: [
        ({ op, descriptor }) => op === Operation.Modify && descriptor === 'action.outcome.metadata',
      ],
      process: (modifiedOutcomeChanges: OutcomeActionMetadataModifiedChange[]) => {
        const modifiedOutcomeEntries: BufferEntry[] = modifiedOutcomeChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const { actionPublicId, value } = change;
            const {
              prev: prevOutcomeMetadata,
              next: nextOutcomeMetadata,
            } = value;

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            const outcomePrecedenceChangedSubtitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.outcomePrecedenceModifiedSubtitle(),
            };

            const outcomePrecedenceChangedValueEntry: BufferEntry = {
              actionPublicId,
              line: templates.outcomePrecedenceModifiedValue({
                next: nextOutcomeMetadata.precedenceIndex,
                prev: prevOutcomeMetadata.precedenceIndex,
              }),
            };

            return [
              outcomePrecedenceChangedSubtitleEntry,
              outcomePrecedenceChangedValueEntry,
              emptyLineEntry,
            ];
          }));


        entriesBuffer.append(modifiedOutcomeEntries);
      },
    }),
    workflowRoutingActionMetadataModified: createChangeProcessor({
      predicates: [
        ({ op, descriptor }) => op === Operation.Modify && descriptor === 'action.workflow.metadata',
      ],
      process: (modifiedWorkflowRoutingChanges: WorkflowRoutingActionMetadataModifiedChange[]) => {
        const modifiedWorkflowRoutingEntries: BufferEntry[] = modifiedWorkflowRoutingChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const { actionPublicId, value } = change;
            const {
              prev: prevWorkflowRoutingMetadata,
              next: nextWorkflowRoutingMetadata,
            } = value;

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            const workflowRoutingPrecedenceChangedSubtitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.workflowRoutingPrecedenceModifiedSubtitle(),
            };

            const workflowRoutingPrecedenceChangedValueEntry: BufferEntry = {
              actionPublicId,
              line: templates.workflowRoutingPrecedenceModifiedValue({
                next: nextWorkflowRoutingMetadata.precedenceIndex,
                prev: prevWorkflowRoutingMetadata.precedenceIndex,
              }),
            };

            return [
              workflowRoutingPrecedenceChangedSubtitleEntry,
              workflowRoutingPrecedenceChangedValueEntry,
              emptyLineEntry,
            ];
          }));


        entriesBuffer.append(modifiedWorkflowRoutingEntries);
      },
    }),
    publishedAttributeAdded: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => op === Operation.Add && descriptor === 'publishedAttribute',
      ],
      process: (
        addedPublishedAttributeChanges: PublishedAttributeAddedChange[],
      ) => {
        const publishedAttributeAddedEntries: BufferEntry[] = addedPublishedAttributeChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const {
              actionPublicId, value: {
                next: nextPublishedAttribute,
              },
            } = change;

            /**
             * the user can add a PA action for a PA that already exists,
             * so we format those differently.
             * splitting that case into its own processor could potentially be better, but it's
             * not necessary for now.
             */
            const isExistingPublishedAttribute = Boolean(propsA.publishedAttributes
              .find(pa => pa.attributeToken === nextPublishedAttribute.attributeToken));

            const subtitleTemplate = isExistingPublishedAttribute
              ? templates.publishedAttributeExistingSubtitle
              : templates.publishedAttributeNewSubtitle;

            const publishedAttributeSubtitleEntry: BufferEntry = {
              actionPublicId,
              line: subtitleTemplate(),
            };

            const valueTemplate = isExistingPublishedAttribute
              ? templates.publishedAttributeExistingValue
              : templates.publishedAttributePublishValue;


            const entriesForPAMetadata: BufferEntry[] = SUPPORTED_PA_METADATA_FIELDS.map(field => {
              const nextValue = nextPublishedAttribute[field];
              return {
                actionPublicId,
                line: valueTemplate({
                  field,
                  value: nextValue,
                }),
              };
            });


            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            return [
              publishedAttributeSubtitleEntry,
              ...entriesForPAMetadata,
              emptyLineEntry,
            ];
          }));

        entriesBuffer.append(publishedAttributeAddedEntries);
      },
    }),
    publishedAttributeModified: createChangeProcessor({
      predicates: [
        ({ op, descriptor }) => op === Operation.Modify && descriptor === 'publishedAttribute',
      ],
      process: (
        modifiedPAChanges: PublishedAttributeModifiedChange[],
      ) => {
        const modifiedPAEntries: BufferEntry[] = modifiedPAChanges
          .flatMap(change => tryBuildEntries(change.actionPublicId, () => {
            const {
              actionPublicId,
              value: {
                prev: prevPAMetadata,
                next: nextPAMetadata,
              },
            } = change;

            const entriesForPAMetadata: BufferEntry[] = Object.entries(nextPAMetadata)
              .map(([field, nextFieldValue]) => {
                const prevFieldValue = prevPAMetadata[field];
                return {
                  actionPublicId,
                  line: templates.publishedAttributeModifiedValue({
                    field,
                    next: nextFieldValue,
                    prev: prevFieldValue,
                  }),
                };
              });

            const publishedAttributeSubtitleEntry: BufferEntry = {
              actionPublicId,
              line: templates.publishedAttributeNewSubtitle(),
            };

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

            return [
              publishedAttributeSubtitleEntry,
              ...entriesForPAMetadata,
              emptyLineEntry,
            ];
          }));

        entriesBuffer.append(modifiedPAEntries);
      },
    }),
    matrixModelActionAddedOrRemoved: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ descriptor, op, value }) => op === Operation.Add && descriptor === 'action'
          && isActionOfType(value.next, ActionType.MATRIX_MODEL),
        ({ descriptor, op, value }) => op === Operation.Remove && descriptor === 'action'
          && isActionOfType(value.prev, ActionType.MATRIX_MODEL),
      ],
      process: (
        addedMatrixActionChanges: ActionAddedChange[],
        removedMatrixActionChanges: ActionRemovedChange[],

      ) => {
        const entries: BufferEntry[] = [
          ...addedMatrixActionChanges,
          ...removedMatrixActionChanges,
        ].flatMap(change => tryBuildEntries(change.actionPublicId, () => {
          const {
            actionPublicId,
            value: {
              next: nextAction,
              prev: prevAction,
            },
            op,
          } = change;

          const action = op === Operation.Add ? nextAction : prevAction;
          const emptyLineEntry = buildEmptyLineEntry(actionPublicId);

          const dataTypeSubtitleEntry: BufferEntry = {
            actionPublicId,
            line: templates.matrixMetadataSubtitle({
              field: 'Data Type',
              operation: op,
            }),
          };

          const dataTypeValueEntry: BufferEntry = {
            actionPublicId,
            line: templates.matrixDataTypeValue({ dataType: action.matrixModel.matrixDataType }),
          };

          const dimensionsSubtitleEntry: BufferEntry = {
            actionPublicId,
            line: templates.matrixMetadataSubtitle({
              field: 'Dimensions',
              operation: op,
            }),
          };

          const dimensionsValueEntry: BufferEntry = {
            actionPublicId,
            line: templates.matrixDimensionsValue({
              dimensions: getDimensionNumber(action.matrixModel.axis.x),
            }),
          };

          const cellValuesSubtitleEntry: BufferEntry = {
            actionPublicId,
            line: templates.matrixMetadataSubtitle({
              field: 'Cell Values',
              operation: op,
            }),
          };

          const cellValuesValueEntry: BufferEntry = {
            actionPublicId,
            line: templates.matrixCellValues({ matrix: action.matrixModel.matrix }),
          };

          return [
            dataTypeSubtitleEntry,
            dataTypeValueEntry,
            emptyLineEntry,
            dimensionsSubtitleEntry,
            dimensionsValueEntry,
            emptyLineEntry,
            cellValuesSubtitleEntry,
            cellValuesValueEntry,
            emptyLineEntry,
          ];
        }));

        entriesBuffer.append(entries);
      },
    }),
    matrixModelActionMetadataModified: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => op === Operation.Modify && descriptor === 'action.matrix.metadata',
      ],
      process: (modifiedMatrixMetadataChanges: MatrixModelActionMetadataModifiedChange[]) => {
        const entries: BufferEntry[] = modifiedMatrixMetadataChanges.flatMap(
          change => tryBuildEntries(change.actionPublicId, () => {
            const {
              actionPublicId,
              value: {
                prev: prevActionMetadata,
                next: nextActionMetadata,
              },
              // op is Modify for all changes due to the predicate
              op,
            } = change;

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);
            const metadataEntries: BufferEntry[] = Object.keys(nextActionMetadata)
              .flatMap((field: keyof typeof nextActionMetadata) => {
                const prevValue = prevActionMetadata[field];
                const nextValue = nextActionMetadata[field];

                const subtitleEntry: BufferEntry = {
                  actionPublicId,
                  line: templates.matrixMetadataSubtitle({
                    field: field === 'cellChanges' ? 'Cell Values' : field,
                    operation: op,
                  }),
                };

                let matrixCellChangeEntries: BufferEntry[] = [];
                let matrixConfigurationChangeEntries: BufferEntry[] = [];
                let matrixMetadataChangeEntries: BufferEntry[] = [];

                if (field === 'cellChanges') {
                  if (Object.keys(nextValue[Operation.Add]).length) {
                    const cellValuesAddedSubtitleEntry = {
                      actionPublicId,
                      line: templates.matrixMetadataSubtitle({
                        field: 'Cell Values',
                        operation: Operation.Add,
                      }),
                    };

                    const cellValuesAddedValueEntry = {
                      actionPublicId,
                      line: templates.matrixCellAddedDeletedValue({
                        changesAddedDeleted: nextValue[Operation.Add],
                      }),
                    };

                    matrixCellChangeEntries = [
                      ...matrixCellChangeEntries,
                      cellValuesAddedSubtitleEntry,
                      cellValuesAddedValueEntry,
                      emptyLineEntry,
                    ];
                  }

                  if (Object.keys(nextValue[Operation.Remove]).length) {
                    const cellValuesDeletedSubtitleEntry = {
                      actionPublicId,
                      line: templates.matrixMetadataSubtitle({
                        field: 'Cell Values',
                        operation: Operation.Remove,
                      }),
                    };

                    const cellValuesDeletedValueEntry = {
                      actionPublicId,
                      line: templates.matrixCellAddedDeletedValue({
                        changesAddedDeleted: nextValue[Operation.Remove],
                      }),
                    };

                    matrixCellChangeEntries = [
                      ...matrixCellChangeEntries,
                      cellValuesDeletedSubtitleEntry,
                      cellValuesDeletedValueEntry,
                      emptyLineEntry,
                    ];
                  }

                  if (Object.keys(nextValue[Operation.Modify]).length) {
                    const modifiedFromEntry: BufferEntry = {
                      actionPublicId,
                      line: templates.matrixMetadataModifiedValue({
                        direction: 'from',
                        value: nextValue[Operation.Modify],
                        field,
                      }),
                    };

                    const modifiedToEntry: BufferEntry = {
                      actionPublicId,
                      line: templates.matrixMetadataModifiedValue({
                        direction: 'to',
                        value: nextValue[Operation.Modify],
                        field,
                      }),
                    };

                    matrixCellChangeEntries = [
                      ...matrixCellChangeEntries,
                      subtitleEntry,
                      modifiedFromEntry,
                      modifiedToEntry,
                      emptyLineEntry,
                    ];
                  }
                } else if (field === 'configuration') {
                  const catchAllFieldModifiedEntry: BufferEntry = {
                    actionPublicId,
                    line: templates.matrixMetadataConfigurationModified(),
                  };

                  matrixConfigurationChangeEntries = [
                    subtitleEntry,
                    catchAllFieldModifiedEntry,
                    emptyLineEntry,
                  ];
                } else {
                  const fromEntry: BufferEntry = {
                    actionPublicId,
                    line: templates.matrixMetadataModifiedValue({
                      direction: 'from',
                      value: prevValue,
                      field,
                    }),
                  };

                  const toEntry: BufferEntry = {
                    actionPublicId,
                    line: templates.matrixMetadataModifiedValue({
                      direction: 'to',
                      value: nextValue,
                      field,
                    }),
                  };

                  matrixMetadataChangeEntries = [
                    subtitleEntry,
                    fromEntry,
                    toEntry,
                    emptyLineEntry,
                  ];
                }

                return [
                  ...matrixCellChangeEntries,
                  ...matrixConfigurationChangeEntries,
                  ...matrixMetadataChangeEntries,
                ];
              });

            return metadataEntries;
          }));

        entriesBuffer.append(entries);
      },
    }),
    matrixModelActionOptionsModified: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => op === Operation.Modify && descriptor === 'action.matrix.options',
      ],
      process: (modifiedMatrixOptionsChanges: MatrixModelActionOptionsModifiedChange[]) => {
        const entries: BufferEntry[] = modifiedMatrixOptionsChanges.flatMap(
          change => tryBuildEntries(change.actionPublicId, () => {
            const {
              actionPublicId,
              value: {
                prev: prevActionOptions,
                next: nextActionOptions,
              },
            } = change;

            const emptyLineEntry = buildEmptyLineEntry(actionPublicId);
            const matrixOptionEntries: BufferEntry[] = Object.keys(nextActionOptions)
              .flatMap((field: keyof typeof nextActionOptions) => {
                const prevOptions = prevActionOptions[field];
                const nextOptions = nextActionOptions[field];

                const matrixOptionChangeEntries: BufferEntry[] = [];
                if (field === VALUE_RANGE_OPTION_FIELD) {
                  const outputValueRangeEntries: BufferEntry[] = [];
                  const prevOutputValueRanges = prevOptions ?? [];
                  const nextOutputValueRanges = nextOptions ?? [];

                  const existingRangeRows = nextOutputValueRanges
                    .slice(0, prevOutputValueRanges.length);
                  const newRangeRows = nextOutputValueRanges
                    .slice(prevOutputValueRanges.length);
                  const deletedRangeRows = prevOutputValueRanges
                    .slice(nextOutputValueRanges.length);

                  existingRangeRows.forEach((valueRangeEntry, index) => {
                    const prev = prevOutputValueRanges[index];
                    const didRangeRowChange = !isEqual(prev, valueRangeEntry);
                    if (prev && didRangeRowChange) {
                      outputValueRangeEntries.push({
                        actionPublicId,
                        line: templates.outputValueRangeRowSubtitle({
                          row: index + 1,
                          op: Operation.Modify,
                        }),
                      });
                      outputValueRangeEntries.push({
                        actionPublicId,
                        line: templates.outputValueRangeValueModified({
                          value: prev,
                          comparisonValue: valueRangeEntry,
                          comparisonSide: 'From',
                        }),
                      });
                      outputValueRangeEntries.push({
                        actionPublicId,
                        line: templates.outputValueRangeValueModified({
                          value: valueRangeEntry,
                          comparisonValue: prev,
                          comparisonSide: 'To',
                        }),
                      });
                    }
                  });

                  if (newRangeRows.length > 0) {
                    outputValueRangeEntries.push({
                      actionPublicId,
                      line: templates.outputValueRangeRowSubtitle({
                        op: Operation.Add,
                      }),
                    });
                    newRangeRows.forEach((valueRangeEntry, index) => {
                      outputValueRangeEntries.push({
                        actionPublicId,
                        line: templates.outputValueRangeValueAdded({
                          row: existingRangeRows.length + index + 1,
                          value: valueRangeEntry,
                        }),
                      });
                    });
                  }

                  if (nextOutputValueRanges.length === 0) {
                    outputValueRangeEntries.push({
                      actionPublicId,
                      line: templates.outputValueRangeRemoved(),
                    });
                  } else if (deletedRangeRows.length > 0) {
                    outputValueRangeEntries.push({
                      actionPublicId,
                      line: templates.outputValueRangeRowSubtitle({
                        op: Operation.Remove,
                      }),
                    });
                    const removedRows = deletedRangeRows.map(
                      (_, index) => existingRangeRows.length + index + 1,
                    );
                    outputValueRangeEntries.push({
                      actionPublicId,
                      line: templates.outputValueRangeValueRemoved({
                        rows: removedRows,
                      }),
                    });
                  }

                  return [...outputValueRangeEntries, emptyLineEntry];
                }

                return [
                  ...matrixOptionChangeEntries,
                ];
              });

            return matrixOptionEntries;
          }));

        entriesBuffer.append(entries);
      },
    }),
    alloySdk: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ op, descriptor }) => op === Operation.Add && descriptor === 'sdk',
        ({ op, descriptor }) => op === Operation.Remove && descriptor === 'sdk',
        ({ op, descriptor }) => op === Operation.Modify && descriptor === 'sdk',
      ],
      process: (
        addedSdkPluginChanges: SdkConfigurationAddedChange[],
        removedSdkPluginChanges: SdkConfigurationRemovedChange[],
        modifiedSdkConfigChanges: SdkConfigurationModifiedChange[],
      ) => {
        const [addedSdkPluginChange] = addedSdkPluginChanges;
        const [removedSdkPluginChange] = removedSdkPluginChanges;
        const [modifiedSdkConfigChange] = modifiedSdkConfigChanges;


        const result = tryBuildEntries(FAKE_ACTION_ID_FOR_SDK, () => {
          let removedSdkPluginEntries: BufferEntry[] = [];
          if (removedSdkPluginChange) {
            const removedSdkPluginEntry: BufferEntry = {
              actionPublicId: FAKE_ACTION_ID_FOR_SDK,
              line: templates.sdkPluginRemoved({
                pluginName: removedSdkPluginChange.value.prev,
              }),
            };

            removedSdkPluginEntries = [removedSdkPluginEntry];
          }

          let addedSdkPluginEntries: BufferEntry[] = [];
          if (addedSdkPluginChange) {
            const {
              value: {
                next: {
                  plugin,
                  populatedFields,
                },
              },
            } = addedSdkPluginChange;


            const addedPluginEntry: BufferEntry = {
              actionPublicId: FAKE_ACTION_ID_FOR_SDK,
              line: templates.sdkPluginAdded({ pluginName: plugin }),
            };

            const addedFieldEntries: BufferEntry[] = Object.values(populatedFields)
              .map(nextField => ({
                actionPublicId: FAKE_ACTION_ID_FOR_SDK,
                line: templates.sdkFieldModified({
                  nextField,
                }),
              }));

            addedSdkPluginEntries = [
              addedPluginEntry,
              ...addedFieldEntries,
            ];
          }


          let modifiedSdkPluginEntries: BufferEntry[] = [];
          if (modifiedSdkConfigChange && !addedSdkPluginChange && !removedSdkPluginChange) {
            modifiedSdkPluginEntries = Object.values(modifiedSdkConfigChange.value.next)
              .map(nextField => ({
                actionPublicId: FAKE_ACTION_ID_FOR_SDK,
                line: templates.sdkFieldModified({
                  nextField,
                }),
              }));
          }

          const subtitleEntry: BufferEntry = {
            actionPublicId: FAKE_ACTION_ID_FOR_SDK,
            line: templates.sdkPluginSubtitle(),
          };

          const emptyLineEntry = buildEmptyLineEntry(FAKE_ACTION_ID_FOR_SDK);

          return [
            subtitleEntry,
            ...removedSdkPluginEntries,
            ...addedSdkPluginEntries,
            ...modifiedSdkPluginEntries,
            emptyLineEntry,
          ];
        });


        entriesBuffer.append(result);
      },
    }),
    failureHandlingMode: createChangeProcessor({
      predicates: [
        ({ op, descriptor }) => op === Operation.Modify && descriptor === 'failureHandling',
      ],
      process: (
        modifiedFailureHandlingModeChanges: FailureHandlingModeModifiedChange[],
      ) => {
        const [modifiedFailureHandlingModeChange] = modifiedFailureHandlingModeChanges;


        const result = tryBuildEntries(FAKE_ACTION_ID_FOR_FAILURE_HANDLING_MODE, () => {
          const modifiedFailureHandlingModeEntry: BufferEntry = {
            actionPublicId: FAKE_ACTION_ID_FOR_FAILURE_HANDLING_MODE,
            line: templates.failureHandlingModeModified({
              nextField: modifiedFailureHandlingModeChange.value.next,
            }),
          };
          const subtitleEntry: BufferEntry = {
            actionPublicId: FAKE_ACTION_ID_FOR_FAILURE_HANDLING_MODE,
            line: templates.failureHandlingModeSubtitle(),
          };

          const emptyLineEntry = buildEmptyLineEntry(FAKE_ACTION_ID_FOR_FAILURE_HANDLING_MODE);

          return [
            subtitleEntry,
            modifiedFailureHandlingModeEntry,
            emptyLineEntry,
          ];
        });


        entriesBuffer.append(result);
      },
    }),
  };
}
